import { Button, Checkbox, LoadingOverlay, Text } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { IconFileExport, IconFileImport } from "@tabler/icons-react"
import { motion } from "framer-motion"
import { Package } from "lucide-react"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link, useLocation, useParams } from "react-router-dom"
import {
  CollectionQuery,
  Filter,
  Order,
} from "../../../models/collection.model"
import {
  EntityConfig,
  entityViewMode,
} from "../../../models/entity-config.model"
import { Sale } from "../../../models/sales.model"
import EntityList from "../../../shared/component/entity-list/entity-list.component"
import { useCurrentUser } from "../../../shared/hooks/current-user.hook"
import { dateFormat } from "../../../shared/utility/date-format/date-format"
import { onViewFile } from "../../../shared/utility/view-file"
import StatCard from "../../dashboard/component/stat-cart"
import { exportSales } from "../api/sale.api"
import ExportSalesModalComponent from "../component/export-sales-modal.component"
import {
  useLazyCountSalesQuery,
  useLazyGetSalesQuery,
  useRemoveSalesFileMutation,
} from "../store/sale.query"
import FileViewerModal from "../../../shared/component/file-viewer/file-viewer-modal.component"
import { notification } from "../../../shared/component/notification/utility/notification"

export default function SalePage() {
  const params = useParams()
  const location = useLocation()
  const { t } = useTranslation()
  const [viewMode, setViewMode] = useState<entityViewMode>("list")
  const [openedExport, { open: openExport, close: closeExport }] =
    useDisclosure()
  const [openedImport, { open: openImport, close: closeImport }] =
    useDisclosure()
  const [fileOpened, { open: fileOpen, close: fileClose }] = useDisclosure()
  const [selectedFiles, setSelectedFiles] = useState<string[]>([])

  const [collection, setCollection] = useState<CollectionQuery>({
    skip: 0,
    top: 10,
    includes: ["property", "unit"],
    orderBy: [{ field: "createdAt", direction: "desc" }],
  })

  const [getSales, sales] = useLazyGetSalesQuery()
  const [countSales, totalSales] = useLazyCountSalesQuery()
  const [countActiveSales, activeSales] = useLazyCountSalesQuery()
  const [countInactiveSales, inactiveSales] = useLazyCountSalesQuery()
  const [userInfo, userId, ownerInfo] = useCurrentUser()
  const [removeSalesFile, removeSalesFileResponse] =
    useRemoveSalesFileMutation()
  const generateFilterOption = (value: boolean): Filter[][] => {
    if (userInfo.role.key == "super_admin") {
      // return [[{ field: "enabled", value: value as boolean, operator: "=" }]]
    }
    return [
      // [{ field: "enabled", value: value as boolean, operator: "=" }],
      // [{ field: "ownerId", value: ownerInfo?.parentOwnerId, operator: "=" }],
    ]
  }
  useEffect(() => {
    if (userInfo?.role?.key == "super_admin") {
      getSales({
        ...collection,
        // filter: [
        //   [{ field: "owner.confidential", value: false, operator: "=" }],
        // ],
      })
    } else {
      getSales({
        ...collection,
        // filter: [
        //   [
        //     {
        //       field: "ownerId",
        //       value: ownerInfo?.parentOwnerId,
        //       operator: "=",
        //     },
        //   ],
        // ],
      })
    }
  }, [collection])
  useEffect(() => {
    if (userInfo.role.key == "super_admin") {
      countSales({
        count: true,
      })
    } else {
      countSales({
        count: true,
        // filter: [
        //   [
        //     {
        //       field: "ownerId",
        //       value: ownerInfo?.parentOwnerId,
        //       operator: "=",
        //     },
        //   ],
        // ],
      })
    }
    countActiveSales({
      count: true,
      filter: generateFilterOption(true),
    })
    countInactiveSales({
      count: true,
      filter: generateFilterOption(false),
    })
  }, [])

  useEffect(() => {
    if (params?.id !== undefined) {
      setViewMode("detail")
    } else {
      setViewMode("list")
    }
    if (location.pathname === "/tenant/archived") {
      setViewMode("detail")
    }
  }, [setViewMode, params?.id, location])

  const data: Sale[] | undefined = sales.data?.data

  const config: EntityConfig<Sale> = {
    primaryColumn: {
      key: "name",
      name: t("name"),
      render: (sale: Sale) => {
        return (
          <Text size="sm" className={sale?.deletedAt ? "text-danger" : ""}>
            {sale?.property?.name} - {sale?.unit?.name}
          </Text>
        )
      },
    },
    detailUrl: "detail",
    rootUrl: "/sale",
    identity: "id",
    name: "name",
    showDetail: true,
    visibleColumn: [
      {
        key: "name",
        name: t("name"),
        render: (sale: Sale) => {
          return (
            <Text size="sm" className={sale?.deletedAt ? "text-danger" : ""}>
              {sale?.property?.name} - {sale?.unit?.name}
            </Text>
          )
        },
      },
      { key: "amount", name: t("amount") },
      {
        key: "soldAt",
        name: t("soldAt"),
        render: (value: Sale) => {
          return dateFormat(value?.soldAt, {
            day: true,
            weekday: true,
            hour: false,
            minute: false,
          })
        },
      },
      { key: "buyerName", name: t("buyerName") },
      { key: "buyerPhone", name: t("buyerPhone") },
      {
        key: "property",
        name: t("property", { count: 1 }),
        render(value) {
          return <>{value?.property?.name}</>
        },
      },
      {
        key: "unit",
        name: t("unit", { count: 1 }),
        render(value) {
          return <>{value?.unit?.name}</>
        },
      },
      {
        key: "file",
        name: t("file"),
        render(value) {
          return (
            <>
              {value?.minioFiles && (
                <span
                  // target="_blank"
                  // rel="noopener noreferrer"
                  // href={tenant?.data?.minoioIdentification}
                  onClick={() => {
                    fileOpen()
                    setSelectedFiles(value?.minioFiles as string[])
                  }}
                  className=" cursor-pointer  text-white inline-block bg-primary  text-sm px-3 py-1 rounded-full"
                >
                  {t("view")}
                </span>
              )}
            </>
          )
        },
      },
    ],
  }

  function handleFileRemoval(item: string) {
    removeSalesFile({
      saleId: sales?.data?.id as string,
      filename: item,
    }).then((response) => {
      notification("success", "File removed Successfully")
    })
  }
  return (
    <main className="max-w-7xl mx-auto py-6 px-4 lg:px-8 mt-16">
      <LoadingOverlay visible={sales.isFetching} />

      <motion.div
        className="hidden md:grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 mb-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1 }}
      >
        <StatCard
          name={t("totalSales")}
          icon={Package}
          value={totalSales?.data?.count}
          color="#6366F1"
        />
        {/* <StatCard
          name={t("activeTenants")}
          icon={TrendingUp}
          value={activeSales?.data?.count}
          color="#8B5CF6"
        />
        <StatCard
          name={t("inactiveTenants")}
          icon={AlertTriangle}
          value={inactiveSales?.data?.count}
          color="#EC4899"
        /> */}
        {/* <StatCard name='Total Revenue' icon={DollarSign} value='12.5%' color='#10B981' /> */}
      </motion.div>
      <EntityList
        viewMode={viewMode}
        title={t("sale", { count: 2 })}
        showExport={true}
        // header={
        //   <>
        //     <div className="h-14 dark:border-gray-500 dark:bg-gray-500 flex items-center justify-between p-2 border-0 border-b">
        //       <div className="h-full flex items-center space-x-4 font-semibold dark:text-white">
        //         {t("sale", { count: 2 })}
        //       </div>
        //       {
        //         <div className="h-full  space-x-4 flex items-center">
        //           {viewMode == "list" && (
        //             <>
        //               <Checkbox
        //                 size="sm"
        //                 onChange={(e) =>
        //                   setCollection({
        //                     ...collection,
        //                     withArchived: e.currentTarget.checked
        //                       ? true
        //                       : false,
        //                   })
        //                 }
        //                 label={
        //                   <span className="text-xs text-gray-500 dark:text-gray-400">
        //                     {t("showArchived")}
        //                   </span>
        //                 }
        //               />

        //               <Button
        //                 classNames={{ label: "flex space-x-2 text-white" }}
        //                 className="bg-primary dark:bg-dark_primary"
        //                 size="xs"
        //                 type="button"
        //                 onClick={(e) => openExport()}
        //                 variant="filled"
        //               >
        //                 <span className="text-white">
        //                   <IconFileExport />
        //                 </span>
        //                 <span>{t("export")}</span>
        //               </Button>
        //               <Button
        //                 classNames={{ label: "flex space-x-2 text-white" }}
        //                 className="bg-primary dark:bg-dark_primary"
        //                 size="xs"
        //                 type="button"
        //                 onClick={(e) => openImport()}
        //                 variant="filled"
        //               >
        //                 <span className="text-white">
        //                   <IconFileImport />
        //                 </span>
        //                 <span>{t("import")}</span>
        //               </Button>
        //             </>
        //           )}
        //           <Link to={`/tenant/new`} state={{ id: location?.state?.id }}>
        //             <Button
        //               classNames={{ label: "flex space-x-2 text-white" }}
        //               className="bg-primary dark:bg-dark_primary"
        //               size="xs"
        //               variant="filled"
        //             >
        //               <span className="text-white">
        //                 <svg
        //                   xmlns="http://www.w3.org/2000/svg"
        //                   className="fill-current h-4 text-white"
        //                   viewBox="0 0 24 24"
        //                 >
        //                   <path
        //                     fillRule="evenodd"
        //                     d="M11 2L11 11L2 11L2 13L11 13L11 22L13 22L13 13L22 13L22 11L13 11L13 2Z"
        //                   />
        //                 </svg>
        //               </span>
        //               <span>{t("new")}</span>
        //             </Button>
        //           </Link>
        //         </div>
        //       }
        //     </div>
        //   </>
        // }
        total={sales?.data?.count}
        itemsLoading={sales.isFetching}
        config={config}
        items={data}
        initialPage={1}
        defaultPageSize={10}
        pageSize={[10, 15, 20]}
        collectionQuery={collection}
        onArchivedChecked={(e) =>
          setCollection({
            ...collection,
            withArchived: e,
          })
        }
        onPaginationChange={(skip: number, top: number) => {
          if (collection.skip !== skip || collection.top !== top) {
            setCollection({ ...collection, skip: skip, top: top })
          }
        }}
        onSearch={(data: any) => {
          setCollection({
            ...collection,
            search: data,
            searchFrom: ["name"],
          })
        }}
        onFilterChange={(data: any) => {
          if (collection?.filter || data.length > 0) {
            setCollection({ ...collection, filter: data })
          }
        }}
        onOrder={(data: Order) =>
          setCollection({ ...collection, orderBy: [data] })
        }
        onExport={openExport}
      />

      <ExportSalesModalComponent
        open={openExport}
        opened={openedExport}
        close={closeExport}
        filename={"sales.xlsx"}
        // filteringVariables={[
        //   [
        //     {
        //       field: "enabled",
        //       value: true,
        //       name: t("active"),
        //       operator: "=",
        //     },
        //     {
        //       field: "enabled",
        //       value: false,
        //       name: t("inactive"),
        //       operator: "=",
        //     },
        //   ],
        //   [
        //     {
        //       field: "gender",
        //       value: "male",
        //       name: t("male"),
        //       operator: "=",
        //     },
        //     {
        //       field: "gender",
        //       value: "female",
        //       name: t("female"),
        //       operator: "=",
        //     },
        //   ],
        // ]}
        exportFunction={exportSales}
      />

      <FileViewerModal
        open={fileOpen}
        close={fileClose}
        fileType={t("sale", { count: 1 })}
        files={selectedFiles as string[]}
        opened={fileOpened}
        remove={handleFileRemoval}
        removeLoading={removeSalesFileResponse?.isLoading}
        fileNames={[]}
      />
    </main>
  )
}
